import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Clock, XCircle, Package, MapPin, CreditCard, Loader2, ArrowLeft, RotateCcw } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
    AppImage,
    Badge,
    Button,
    cn,
    Heading1,
    Link,
    Paragraph,
    type TrackingEvent
} from '@shopmuchmore/design-system';

import { useClearCart } from '../../cart/cart.services';
import { OrderCancellationModal } from '../../checkout/components/OrderCancellationModal';
import { OrderTrackingSheet } from '../components/OrderTrackingSheet';
import { useOrderDetailQuery, useShipmentTrackingQuery } from '../orders.services';
import { formatPrice } from '@shopmuchmore/common-utils';

export function OrderDetailsPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { orderNumber } = useParams();
    const isAccountView = location.pathname.includes('/account');

    // API Queries
    const { data: order, isLoading } = useOrderDetailQuery(orderNumber || '');
    
    // Tracking Sheet State
    const [trackingOpen, setTrackingOpen] = useState(false);
    const [activeShipmentNumber, setActiveShipmentNumber] = useState<string | null>(null);

    const { data: trackingData, isLoading: isTrackingLoading } = useShipmentTrackingQuery(
        orderNumber || '', 
        activeShipmentNumber || '', 
        { enabled: !!orderNumber && !!activeShipmentNumber && trackingOpen }
    );
    
    const { mutate: clearCart } = useClearCart();
    
    // Check if any shipment is already in transit or delivered
    const hasInTransitShipments = order?.shipments?.some(shp => {
        const s = shp.status?.toLowerCase();
        return ['in_transit', 'shipped', 'delivered', 'out_for_delivery'].includes(s || '');
    });

    const [timeLeft, setTimeLeft] = useState<number | null>(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    
    // Assuming 'success' if order exists, since new API doesn't have a top-level status
    const status: string = 'success';
    const isSuccessState = ['success', 'completed'].includes(status);
    const isFailedState = ['failed', 'cancelled'].includes(status);
    const isPendingState = status === 'pending';
    const isRefundedState = status === 'refunded';

    // Clear cart on mount if it's a successful order view for the first time
    useEffect(() => {
        if (isSuccessState && !isAccountView) {
            clearCart();
        }
    }, [isSuccessState, isAccountView, clearCart]);

    // Timer Logic - 3 minutes from order_date
    useEffect(() => {
        const orderTime = order?.order_date;
        if (!orderTime || !isSuccessState) return;

        const calculateTimeLeft = () => {
            const startTime = new Date(orderTime).getTime();
            const now = new Date().getTime();
            const diff = Math.floor((startTime + 180000 - now) / 1000); // 180000ms = 3 mins
            return Math.max(0, diff);
        };

        const initialTime = calculateTimeLeft();
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setTimeLeft(initialTime);

        if (initialTime <= 0) return;

        const timer = setInterval(() => {
            const remaining = calculateTimeLeft();
            setTimeLeft(remaining);
            if (remaining <= 0) clearInterval(timer);
        }, 1000);

        return () => clearInterval(timer);
    }, [order?.order_date, isSuccessState]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (isLoading) {
        return (
            <div className={isAccountView ? "py-12" : "min-h-screen bg-gray-50/50 flex flex-col items-center justify-center p-4"}>
                <Loader2 className="w-10 h-10 text-secondary animate-spin mb-4 mx-auto" />
                <Paragraph className="text-gray-500 font-bold animate-pulse text-center">Loading order details...</Paragraph>
            </div>
        );
    }

    if (!order) {
        return (
            <div className={isAccountView ? "py-12" : "min-h-screen bg-gray-50/50 flex flex-col items-center justify-center p-4 relative overflow-hidden"}>
                {!isAccountView && (
                    <>
                        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48" />
                        <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl -ml-48 -mb-48" />
                    </>
                )}

                <motion.div 
                    initial={{ scale: 0.9, opacity: 0, y: 20 }} 
                    animate={{ scale: 1, opacity: 1, y: 0 }} 
                    className="bg-white/70 backdrop-blur-xl rounded-[48px] p-12 md:p-16 max-w-xl w-full text-center border border-white shadow-2xl shadow-gray-200/50 mx-auto relative z-10"
                >
                    <div className="w-24 h-24 bg-slate-900 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl rotate-6">
                        <XCircle className="w-12 h-12 text-primary" strokeWidth={2.5} />
                    </div>
                    
                    <h2 className="text-4xl font-black text-gray-900 mb-4 tracking-tight">Order Status: Ghostly</h2>
                    <p className="text-gray-500 mb-10 max-w-xs mx-auto font-medium leading-relaxed">
                         We couldn't track down order <span className="text-secondary font-black">#{orderNumber}</span>. It might be missing in action or the link has expired.
                    </p>
                    
                    <div className="flex flex-col gap-3">
                        <Button 
                            onClick={() => navigate('/account/orders')} 
                            className="w-full h-16 rounded-2xl bg-gray-900 text-white hover:bg-black font-black text-lg shadow-xl shadow-gray-300/50"
                        >
                            Return to My Orders
                        </Button>
                        <Button 
                            variant="ghost" 
                            onClick={() => navigate('/')}
                            className="text-gray-400 font-bold hover:text-gray-900"
                        >
                            Go to Homepage
                        </Button>
                    </div>
                </motion.div>
            </div>
        );
    }

    const containerStyles = isAccountView ? "pb-20" : "min-h-screen bg-gray-50/50 pb-20 pt-10 px-4";
    
    const getBadgeVariant = (s: string) => {
        const lower = s.toLowerCase();
        if (['success', 'completed'].includes(lower)) return 'secondary';
        if (['failed', 'cancelled'].includes(lower)) return 'destructive';
        if (lower === 'pending') return 'outline';
        if (lower === 'refunded') return 'outline';
        return 'default';
    };

    // Tracking Events Transformation
    let trackingEvents: TrackingEvent[] = [];
    if (trackingData?.tracking_timeline) {
        const tl = trackingData.tracking_timeline;
        const stages = [
            { key: 'order_placed', title: 'Order Placed', ...tl.order_placed },
            { key: 'order_accepted', title: 'Order Accepted', ...tl.order_accepted },
            { key: 'transport', title: 'Transport', ...tl.transport },
            { key: 'delivered', title: 'Delivered', ...tl.delivered },
        ];

        trackingEvents = stages.map((stage) => {
            const timeParts = stage.time ? stage.time.split(' ') : [];
            const dateStr = timeParts.length >= 3 ? `${timeParts[0]} ${timeParts[1]} ${timeParts[2]}` : '';
            const timeStr = timeParts.length >= 5 ? `${timeParts[3]} ${timeParts[4]}` : '';
            
            return {
                status: stage.title,
                description: stage.label,
                location: '',
                date: dateStr,
                time: timeStr,
                isCompleted: stage.status,
            };
        });
        
        const firstIncomplete = trackingEvents.findIndex(e => !e.isCompleted);
        if (firstIncomplete > 0) {
            trackingEvents[firstIncomplete - 1].isCurrent = true;
        } else if (firstIncomplete === -1 && trackingEvents.length > 0) {
            trackingEvents[trackingEvents.length - 1].isCurrent = true;
        }
    }

    return (
        <div className={containerStyles}>
            <div className={isAccountView ? "w-full" : "max-w-4xl mx-auto"}>
                <Link href="/account/orders" variant="link" className="inline-flex items-center gap-2 text-gray-400 font-bold mb-8 hover:text-gray-900 transition-colors no-underline">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Orders
                </Link>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Main Content */}
                    <div className="flex-1 space-y-6">
                        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-white rounded-[40px] p-8 md:p-12 border border-gray-100 overflow-hidden relative">
                            <div className="relative z-10 text-center md:text-left">
                                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto md:mx-0 
                                    ${isSuccessState ? 'bg-green-50' : 
                                      isFailedState ? 'bg-destructive/5' : 
                                      isPendingState ? 'bg-amber-50' : 
                                      isRefundedState ? 'bg-purple-50' : 'bg-gray-50'}`}
                                >
                                    {isSuccessState && <Check className="w-10 h-10 text-green-500" strokeWidth={3} />}
                                    {isFailedState && <XCircle className="w-10 h-10 text-destructive" strokeWidth={3} />}
                                    {isPendingState && <Clock className="w-10 h-10 text-amber-500" strokeWidth={3} />}
                                    {isRefundedState && <RotateCcw className="w-10 h-10 text-purple-500" strokeWidth={3} />}
                                    {(!isSuccessState && !isFailedState && !isPendingState && !isRefundedState) && <Package className="w-10 h-10 text-gray-400" strokeWidth={2} />}
                                </div>
                                <Heading1 className="text-3xl md:text-4xl font-black text-gray-900 mb-2">
                                    {isSuccessState ? 'Order Confirmed' : 
                                     isFailedState ? (status === 'cancelled' ? 'Order Cancelled' : 'Payment Failed') : 
                                     isPendingState ? 'Payment Pending' : 
                                     isRefundedState ? 'Order Refunded' : 'Order Details'}
                                </Heading1>
                                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-4">
                                    <Badge variant="outline" className="px-4 py-2 rounded-full border-gray-100 text-gray-400 font-bold bg-gray-50">
                                        #{order.order_number}
                                    </Badge>
                                    <Badge 
                                        variant={getBadgeVariant(status)} 
                                        className={`px-4 py-2 rounded-full font-black uppercase text-[10px] tracking-wider ${status === 'refunded' ? 'bg-purple-600 text-white border-transparent' : ''}`}
                                    >
                                        {status.replace(/_/g, ' ')}
                                    </Badge>
                                </div>
                            </div>
                        </motion.div>

                        <div className="space-y-6">
                            {order.shipments?.map((shp, idx) => (
                                <div key={idx} className="bg-white rounded-[32px] overflow-hidden border border-gray-100">
                                    <div className="p-6 border-b border-gray-100 flex flex-col bg-gray-50/20 gap-4">
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-3">
                                                <Package className="w-5 h-5 text-gray-400 shrink-0" />
                                                <h3 className="font-black text-gray-900 uppercase tracking-tight">Shipment #{shp.shipment_number}</h3>
                                            </div>
                                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-8">{shp.carrier_code || 'Unknown Carrier'}</p>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 ml-8">
                                            <Badge 
                                                variant="outline"
                                                className={cn(
                                                    "px-4 py-1.5 rounded-full font-black uppercase text-[10px] tracking-wider",
                                                    shp.status?.toLowerCase() === 'delivered' ? "bg-green-100 text-green-700 border-green-200" : "bg-purple-100 text-purple-700 border-purple-200"
                                                )}
                                            >
                                                {shp.status ? shp.status.replace(/_/g, ' ') : 'Processing'}
                                            </Badge>
                                            {shp.status?.toLowerCase() !== 'declined' && (
                                                <Button 
                                                    size="sm" 
                                                    variant="outline" 
                                                    className="rounded-xl h-9 border-slate-200 font-bold text-xs whitespace-nowrap"
                                                    onClick={() => {
                                                        setActiveShipmentNumber(shp.shipment_number);
                                                        setTrackingOpen(true);
                                                    }}
                                                >
                                                    <MapPin className="size-3 mr-1.5" /> Track Parcel
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="divide-y divide-gray-50">
                                        {shp.items?.map((item, iIdx) => (
                                            <div key={iIdx} className="p-6">
                                                <div className="flex gap-6 items-start">
                                                    <AppImage src={item.product_image} alt={item.product_name} size="md" className="rounded-sm" />
                                                    <div className="flex-1 min-w-0 pt-1">
                                                        <h4 className="font-bold text-gray-900 line-clamp-2 whitespace-normal overflow-hidden">{item.product_name}</h4>
                                                        <p className="text-xs text-gray-400 mt-1.5 font-black uppercase tracking-widest">Qty: {item.quantity}</p>
                                                    </div>
                                                    <div className="text-right flex flex-col items-end gap-2">
                                                        <p className="font-black text-gray-900">{formatPrice(item.subtotal.toString())}</p>
                                                        {(shp.status?.toLowerCase() === 'delivered' || shp.status?.toLowerCase() === 'completed') && (
                                                            <>
                                                                 { (item.reviewed || (item as any).is_reviewed) ? (
                                                                     <Badge variant="secondary">
                                                                         Reviewed
                                                                     </Badge>
                                                                 ) : (
                                                                     (item.product_slug || item.slug || item.productSlug || item.product_id || item.id || item.productId || item.product?.product_slug || item.product?.slug || item.product?.product_id || item.product?.id) && (
                                                                         <Link 
                                                                             href={`/account/reviews/write?productSlug=${item.product_slug || item.slug || item.productSlug || item.product?.product_slug || item.product?.slug || ''}&productId=${item.product_id || item.id || item.productId || item.product?.product_id || item.product?.id || ''}&orderNo=${order.order_number}`}
                                                                             variant="outline"
                                                                             size="sm"
                                                                             className="h-10 rounded-xl text-[10px] font-black uppercase tracking-widest px-5 border-gray-200 hover:bg-primary/5 hover:text-primary transition-all no-underline"
                                                                         >
                                                                             Write Review
                                                                         </Link>
                                                                     )
                                                                 )}
                                                            </>
                                                        )}
                                                    </div>

                                                </div>
                                            </div>
                                        ))}

                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Sidebar: Details & Summary */}
                    <div className="w-full lg:w-[360px] space-y-6">
                        {/* Cancellation Grace Period */}
                        <AnimatePresence>
                            {(timeLeft !== null && timeLeft > 0 && status === 'success' && !hasInTransitShipments) && (
                                <motion.div 
                                    initial={{ x: 20, opacity: 0 }} 
                                    animate={{ x: 0, opacity: 1 }} 
                                    exit={{ x: 20, opacity: 0 }}
                                    className="bg-destructive/5 border border-destructive/10 rounded-[32px] p-6 relative overflow-hidden"
                                >
                                    <div className="absolute -top-4 -right-4 w-24 h-24 bg-destructive/10 rounded-full blur-2xl" />
                                    <div className="relative z-10">
                                        <div className="flex items-center gap-2 text-destructive mb-4">
                                            <Clock className="w-5 h-5 animate-pulse" />
                                            <span className="text-sm font-black uppercase tracking-wider">Cancellation Window</span>
                                        </div>
                                        <Paragraph className="text-destructive/70 text-sm leading-relaxed mb-6">
                                            Change your mind? You have <span className="font-black text-destructive">{formatTime(timeLeft)}</span> left to cancel this order directly.
                                        </Paragraph>
                                        <Button 
                                            variant="ghost" 
                                            onClick={() => setShowCancelModal(true)}
                                            className="w-full h-12 bg-white text-destructive hover:bg-destructive hover:text-white rounded-2xl font-black border-2 border-destructive/10 transition-all shadow-none"
                                        >
                                            Cancel Order
                                        </Button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Order Summary */}
                        {order.payment_summary && (
                            <div className="bg-white rounded-[32px] p-8 border border-gray-100 space-y-6">
                                <h3 className="font-black text-gray-900 flex items-center gap-2">
                                    <CreditCard className="w-5 h-5 text-gray-400" />
                                    Payment Summary
                                </h3>
                                <div className="space-y-4 pt-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-400 font-bold">Subtotal</span>
                                        <span className="text-gray-900 font-black">{formatPrice(order.payment_summary.subtotal.toString())}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-400 font-bold">Delivery Fee</span>
                                        <span className="text-gray-900 font-black">{formatPrice(order.payment_summary.delivery_fee.toString())}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-400 font-bold">VAT</span>
                                        <span className="text-gray-900 font-black">{formatPrice(order.payment_summary.vat.toString())}</span>
                                    </div>
                                    <div className="h-px bg-gray-50 my-2" />
                                    <div className="flex justify-between items-center pt-2">
                                        <span className="text-gray-900 font-black text-lg">Total Paid</span>
                                        <span className="text-secondary font-black text-2xl tracking-tight">{formatPrice(order.payment_summary.total_paid.toString())}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Shipping Address */}
                        {order.shipping_destination && (
                            <div className="bg-white rounded-[32px] p-8 border border-gray-100 space-y-4">
                                <h3 className="font-black text-gray-900 flex items-center gap-2">
                                    <MapPin className="w-5 h-5 text-gray-400" />
                                    Shipping Destination
                                </h3>
                                <div className="pt-2">
                                    <p className="font-black text-gray-900 text-sm">{order.shipping_destination.first_name} {order.shipping_destination.last_name}</p>
                                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                                        {order.shipping_destination.street_address}<br />
                                        {order.shipping_destination.city}, {order.shipping_destination.state_or_region}<br />
                                        {order.shipping_destination.country}
                                    </p>
                                </div>
                            </div>
                        )}

                        <Button 
                            variant="secondary"
                            fullWidth
                            onClick={() => navigate('/')} 
                        >
                            Return to Shopping
                        </Button>
                    </div>
                </div>
            </div>

            <OrderCancellationModal
                open={showCancelModal}
                orderId={order.order_number}
                onClose={() => setShowCancelModal(false)}
                onConfirm={() => navigate('/cart')}
            />

            <OrderTrackingSheet
                open={trackingOpen}
                onOpenChange={setTrackingOpen}
                activeShipmentNumber={activeShipmentNumber}
                isTrackingLoading={isTrackingLoading}
                trackingData={trackingData}
                trackingEvents={trackingEvents}
            />
        </div>
    );
}
